import styles from './addTestCasesToScenarioModal.module.scss';
import { Checkbox } from "../../shared/components/checkbox/checkbox";
import { MethodType } from "../../shared/components/methodType/methodType";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { Accordion } from "../../shared/components/accordion/accordion";
import {
  TestCase,
  TestCaseTemplate
} from "../../types/testCaseTemplateTypes";
import { SearchInput } from "../../shared/components/searchInput/searchInput";
import arrowIcon from "../../shared/images/arrow.svg";
import templatePositiveIcon from "../../shared/images/templatePositive.svg";
import templateNegativeIcon from "../../shared/images/templateNegative.svg";
import folderIcon from "../../shared/images/folder.svg";
import infoIcon from "../../shared/images/info.svg";
import { FolderWithTemplate } from "../../types/testCaseTemplateFolderTypes";
import { useGetAllFolderQuery } from "../../api/testCaseTemplateFolderService";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import {
  useLazyGetAllFolderTemplatesForSearchQuery,
  useLazyGetAllFolderTemplatesQuery
} from "../../api/testCaseTemplateService";
import spinnerGif from "../../shared/images/spinner.gif";

type AddTestCasesToScenarioModalProps = {
  onClose: (isSave: boolean, selectedTestCasesIds?: Array<number>) => void;
  scenarioName: string;
}

export const AddTestCasesToScenarioModal = ({ onClose, scenarioName }: AddTestCasesToScenarioModalProps) => {
  const { t } = useTranslation();

  const [selectedTestCasesIds, setSelectedTestCasesIds] = useState<Array<number>>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  const [foldersWithTemplates, setFoldersWithTemplates] = useState<Array<FolderWithTemplate>>([]);
  const [filteredFolders, setFilteredFolders] = useState<Array<FolderWithTemplate>>([]);
  const [activeIndex, setActiveIndex] = useState<number>()

  const { data: folders } = useGetAllFolderQuery("", { refetchOnMountOrArgChange: true });
  const [getAllFoldersForSearch, { data: foldersForSearch}] = useLazyGetAllFolderTemplatesForSearchQuery();
  const [getAllFolderTemplates, { data: folderTemplates, isFetching: isFetchingFolderTemplates }] = useLazyGetAllFolderTemplatesQuery();

  const setFolderWithTemplatesAfterFetchingFolders = useCallback(() => {
    if (!!folders) {
      setFoldersWithTemplates((foldersWithTemplates) => folders.map(folder => {
        const findItem = foldersWithTemplates.find(item => item.id === folder.id);
        let templates: Array<TestCaseTemplate> = [];
        if (!!findItem) {
          if (findItem.isFetchingTemplate) {
            templates = findItem.testCaseTemplates;
          }
        }
        return {
          id: folder.id,
          name: folder.name,
          isFetchingTemplate: false,
          templatesCount: folder.templatesCount,
          testCaseTemplates: templates,
          isNotDelete: folder.isNotDelete
        };
      }))
      getAllFoldersForSearch("");
    }
  }, [folders, setFoldersWithTemplates, getAllFoldersForSearch]);

  useEffect(() => {
    setFolderWithTemplatesAfterFetchingFolders();
  }, [setFolderWithTemplatesAfterFetchingFolders]);

  useEffect(() => {
    if (!!foldersForSearch) {
      setFoldersWithTemplates((foldersWithTemplates) => foldersForSearch.map(folder => {
        const findItem = foldersWithTemplates.find(item => item.id === folder.id);
        let testCaseTemplates: Array<TestCaseTemplate> = folder.testCaseTemplates.map(template => ({
          id: template.id,
          name: template.name,
          testCaseTemplateFolderId: template.testCaseTemplateFolderId,
          isPositive: template.isPositive,
          methodId: 0,
          priorityId: 0,
          statusCodeId: 0,
          maximumExecutionTimeout: 0,
          requestHeaders: [],
          environmentServers: [],
          requestParameters: [],
          responseParameters: [],
          isJsonSchemaValidation: false,
          childTestCases: template.childTestCases.map(testCase => ({
            id: testCase.id,
            name: testCase.name,
            testCaseTemplateId: testCase.testCaseTemplateId,
            requestParameterValues: [],
            responseParameterValues: []
          }))
        }));
        if (!!findItem) {
          if (findItem.isFetchingTemplate) {
            testCaseTemplates = findItem.testCaseTemplates;
          }
        }
        return {
          id: folder.id,
          name: folder.name,
          isFetchingTemplate: findItem?.isFetchingTemplate || false,
          templatesCount: folder.templatesCount,
          isNotDelete: folder.isNotDelete,
          testCaseTemplates
        }
      }));
    }
  }, [foldersForSearch, setFoldersWithTemplates]);

  useEffect(() => {
    if ((!!folderTemplates && !isFetchingFolderTemplates) && activeIndex != null) {
      const newFoldersWithTemplate = [...foldersWithTemplates];
      const findFolder = newFoldersWithTemplate.find(item => item.id === filteredFolders[activeIndex].id);
      if (!!findFolder) {
        findFolder.testCaseTemplates = [...folderTemplates];
        findFolder.isFetchingTemplate = true;
        setFoldersWithTemplates(newFoldersWithTemplate);
      }
    }
  }, [folderTemplates, isFetchingFolderTemplates, setFoldersWithTemplates]);

  const getNotEmptyFolders = useCallback((): Array<FolderWithTemplate> => {
    return foldersWithTemplates.map(folder => {
      return {
        ...folder,
        testCaseTemplates: [...folder.testCaseTemplates.filter(template => template.childTestCases.length !== 0)]
      } as FolderWithTemplate
    }).filter(folder => folder.testCaseTemplates.length !== 0);
  }, [foldersWithTemplates]);

  const getFilteredFolders = useCallback((): Array<FolderWithTemplate> => {
    const newFolders: Array<FolderWithTemplate> = [];
    getNotEmptyFolders().forEach(folder => {
      if (folder.name.toLowerCase().includes(searchValue.toLowerCase())) {
        newFolders.push(folder);
      } else {
        const newTemplates: Array<TestCaseTemplate> = [];
        folder.testCaseTemplates.forEach(template => {
          if (template.name.toLowerCase().includes(searchValue.toLowerCase())) {
            newTemplates.push(template);
          } else {
            const newTestCases: Array<TestCase> = [];
            template.childTestCases.forEach(testCase => {
              if (testCase.name.toLowerCase().includes(searchValue.toLowerCase())) {
                newTestCases.push(testCase);
              }
            });
            if (newTestCases.length !== 0) {
              const newTemplate: TestCaseTemplate = {
                ...template,
                childTestCases: [...newTestCases]
              };
              newTemplates.push(newTemplate);
            }
          }
        });
        if (newTemplates.length !== 0) {
          const newFolder: FolderWithTemplate = {
            ...folder,
            testCaseTemplates: [...newTemplates]
          };
          newFolders.push(newFolder);
        }
      }
    });
    return newFolders;
  }, [getNotEmptyFolders, searchValue]);

  useEffect(() => {
    let activeFolder: FolderWithTemplate | undefined;
    if (activeIndex != null) {
      activeFolder = filteredFolders[activeIndex];
    }
    const newFilteredFolders = getFilteredFolders();
    setFilteredFolders(newFilteredFolders);
    if (!!activeFolder) {
      const newActiveIndex = newFilteredFolders.findIndex(item => item.id === activeFolder?.id);
      if (newActiveIndex > -1) {
        setActiveIndex(newActiveIndex);
        if (!newFilteredFolders[newActiveIndex].isFetchingTemplate) {
          getAllFolderTemplates(newFilteredFolders[newActiveIndex].id as number);
        }
      } else if (newFilteredFolders.length === 1 && newFilteredFolders[0].templatesCount !== 0) {
        setActiveIndex(0);
        getAllFolderTemplates(newFilteredFolders[0].id as number);
      } else {
        setActiveIndex(undefined);
      }
    } else if (newFilteredFolders.length === 1 && newFilteredFolders[0].templatesCount !== 0) {
      setActiveIndex(0);
      getAllFolderTemplates(newFilteredFolders[0].id as number);
    }
  }, [setActiveIndex, setFilteredFolders, getFilteredFolders]);

  const onChangeSelect = useCallback((testCase: TestCase) => {
    const newArray = [...selectedTestCasesIds];
    const index = newArray.findIndex(id => id === testCase.id);
    const needRemove = index > -1;
    if (needRemove) {
      newArray.splice(index, 1);
    } else {
      newArray.push(testCase.id as number);
    }

    setSelectedTestCasesIds(newArray);
  }, [selectedTestCasesIds, setSelectedTestCasesIds])

  const isSelected = useCallback((testCase: TestCase): boolean => {
    const findItem = selectedTestCasesIds.find(id => id === testCase.id);
    return !!findItem;
  }, [selectedTestCasesIds]);

  const onClickAdd = useCallback((): void => {
    onClose(true, selectedTestCasesIds);
  }, [onClose, selectedTestCasesIds]);

  const getSelectedTestCasesByTemplate = useCallback((testCaseTemplate: TestCaseTemplate): Array<TestCase> => {
    const result: Array<TestCase> = [];
    testCaseTemplate.childTestCases.forEach(testCase => {
      const index = selectedTestCasesIds.findIndex(id => id === testCase.id);
      if (index > -1) {
        result.push(testCase);
      }
    });
    return result;
  }, [selectedTestCasesIds]);

  const onClickTemplateCheckbox = useCallback((e: MouseEvent<HTMLDivElement>, testCaseTemplate: TestCaseTemplate): void => {
    e.stopPropagation();
    const thisSelectedTestCases = getSelectedTestCasesByTemplate(testCaseTemplate);
    const newSelectedTestCasesIds = [...selectedTestCasesIds];

    thisSelectedTestCases.forEach(testCase => {
      const findIndex = newSelectedTestCasesIds.findIndex(id => id === testCase.id);
      if (findIndex > -1) {
        newSelectedTestCasesIds.splice(findIndex, 1);
      }
    });
    if (thisSelectedTestCases.length !== testCaseTemplate.childTestCases.length) {
      newSelectedTestCasesIds.push(...testCaseTemplate.childTestCases.map(item => item.id as number));
    }
    setSelectedTestCasesIds(newSelectedTestCasesIds);
  }, [selectedTestCasesIds, getSelectedTestCasesByTemplate, setSelectedTestCasesIds]);

  const getSelectedTemplateByFolder = useCallback((folder: FolderWithTemplate): Array<TestCaseTemplate> => {
    const result: Array<TestCaseTemplate> = [];
    folder.testCaseTemplates.forEach(template => {
      const selectedTestCasesByTemplate = getSelectedTestCasesByTemplate(template);
      if (selectedTestCasesByTemplate.length === template.childTestCases.length) {
        result.push(template);
      }
    });
    return result;
  }, [getSelectedTestCasesByTemplate]);

  const onClickFolderCheckbox = useCallback((e: MouseEvent<HTMLDivElement>, folder: FolderWithTemplate): void => {
    e.stopPropagation();
    const thisSelectedTemplates = getSelectedTemplateByFolder(folder);
    const thisSelectedTestCases: Array<TestCase> = [];
    thisSelectedTemplates.forEach(template => {
      thisSelectedTestCases.push(...getSelectedTestCasesByTemplate(template));
    })
    const newSelectedTestCasesIds = [...selectedTestCasesIds];

    thisSelectedTestCases.forEach(testCase => {
      const findIndex = newSelectedTestCasesIds.findIndex(id => id === testCase.id);
      if (findIndex > -1) {
        newSelectedTestCasesIds.splice(findIndex, 1);
      }
    });
    if (thisSelectedTemplates.length !== folder.testCaseTemplates.length) {
      folder.testCaseTemplates.forEach(template => {
        newSelectedTestCasesIds.push(...template.childTestCases.map(item => item.id as number));
      });
    }
    setSelectedTestCasesIds(newSelectedTestCasesIds);
  }, [selectedTestCasesIds, getSelectedTestCasesByTemplate, getSelectedTemplateByFolder, setSelectedTestCasesIds]);

  const changeActiveFolders = useCallback((folderIndex?: number): void => {
    if (activeIndex === folderIndex) {
      setActiveIndex(undefined);
    } else if (folderIndex != undefined) {
      getAllFolderTemplates(filteredFolders[folderIndex].id as number);
    }
    setActiveIndex(folderIndex);
  }, [activeIndex, filteredFolders, setActiveIndex, getAllFolderTemplates])

  return (
    <ModalWindow title={`${t("scenario.addTestCaseTo")} '${scenarioName}'`}
                 onClose={() => onClose(false)}
                 width={814}
                 showTooltip={true}
                 children={
                   <div className={styles.container}>
                     <div className={styles.header}>
                       <div className={styles.search}>
                         <SearchInput value={searchValue}
                                      disabled={!foldersForSearch}
                                      placeholder={!!foldersForSearch ? t("common.search") : t("common.searchIsLoading") }
                                      onChange={setSearchValue}/>
                       </div>
                     </div>

                     <div className={styles.folders}>
                       <Accordion
                         activeClassName={styles.active}
                         activeIndex={activeIndex}
                         onChangeActive={changeActiveFolders}
                         data={
                           filteredFolders.map(folder => ({
                             title: (
                               <div
                                 className={`${styles.folder} ${getSelectedTemplateByFolder(folder).length === folder.testCaseTemplates.length ? styles.selected : ""}`}>
                                 <div className={styles.info}>
                                   <img src={arrowIcon} alt=""
                                        className={styles.arrow}/>
                                   <img src={folderIcon} alt=""/>
                                   <div className={styles.name}>
                                     <span title={folder.name}>{folder.name}</span>
                                   </div>
                                 </div>
                                 {!!folder.testCaseTemplates.length &&
                                   <div className={styles.checkbox} onClick={(e) => onClickFolderCheckbox(e, folder)}>
                                     <Checkbox
                                       isChecked={getSelectedTemplateByFolder(folder).length === folder.testCaseTemplates.length}/>
                                   </div>
                                 }
                               </div>
                             ),
                             child: isFetchingFolderTemplates && !folder.testCaseTemplates.length
                               ? (
                                 <div className={styles.spinner}>
                                   <img src={spinnerGif} alt=""/>
                                 </div>
                               ) : (
                                 <div className={styles.templates}>
                                   <Accordion
                                     activeClassName={styles.active}
                                     disabledAutoScroll
                                     data={
                                       folder.testCaseTemplates.map(testCaseTemplate => ({
                                         title: (
                                           <div
                                             className={`${styles.testCaseTemplate} ${getSelectedTestCasesByTemplate(testCaseTemplate).length === testCaseTemplate.childTestCases.length && testCaseTemplate.childTestCases.length ? styles.selected : ""}`}>
                                             <div className={styles.info}>
                                               {!!testCaseTemplate.childTestCases.length ?
                                                 <img src={arrowIcon} alt=""
                                                      className={styles.arrow}/>
                                                 : <div className={styles.customArrow}></div>
                                               }

                                               <img src={testCaseTemplate.isPositive ? templatePositiveIcon : templateNegativeIcon} alt=""/>
                                               <div className={styles.name}>
                                                 <span title={testCaseTemplate.name}>{testCaseTemplate.name}</span>
                                               </div>
                                               <div className={styles.actions}>
                                                 {!!testCaseTemplate.description && (
                                                   <img title={testCaseTemplate.description}
                                                        className={styles.description}
                                                        src={infoIcon} alt=""/>
                                                 )}
                                               </div>
                                             </div>
                                             { !!testCaseTemplate.childTestCases.length && (
                                               <div className={styles.checkbox}
                                                    onClick={(e) => onClickTemplateCheckbox(e, testCaseTemplate)}>
                                                 <Checkbox
                                                   isChecked={getSelectedTestCasesByTemplate(testCaseTemplate).length === testCaseTemplate.childTestCases.length}/>
                                               </div>
                                             )}

                                           </div>
                                         ),
                                         child: (
                                           <div className={styles.testCases}>
                                             {testCaseTemplate.childTestCases.map(((testCase, indexTestCase) => (
                                               <div key={indexTestCase}
                                                    className={`${styles.testCase} ${isSelected(testCase) ? styles.selected : ""}`}
                                                    onClick={() => onChangeSelect(testCase)}>
                                                 <div className={styles.info}>
                                                   <div className={styles.methodType}>
                                                     <MethodType methodId={testCaseTemplate.methodId}/>
                                                   </div>
                                                   {testCase.name}
                                                 </div>
                                                 <Checkbox isChecked={selectedTestCasesIds.includes(testCase.id as number)}/>
                                               </div>
                                             )))}
                                           </div>
                                         ),
                                         disabled: !testCaseTemplate.childTestCases.length
                                       }))
                                     }/>
                                 </div>
                               )
                           }))
                         }/>
                     </div>
                     <div className={styles.buttons}>
                       <button className={styles.cancel} onClick={() => onClose(false)}>{t("common.cancel")}</button>
                       <button className={styles.save} onClick={onClickAdd}>{t("common.add")}</button>
                     </div>
                   </div>
                 }
    />
  )
}